import React, { useMemo } from "react";
import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import {
  RESOLVE_LOCAL_AUTH_STATE,
  RESOLVE_LOCAL_USER_STATE,
} from "../auth/AuthActions";

import PersonalChange from "./PersonalChange";
import ProfileChange from "./ProfileChange";
import PwdChange from "./PwdChange";

/**
 * Settings page
 * - Left nav with active link styling
 * - Nested routes (kept local for drop-in replace)
 * - Redirects /settings -> /settings/profile-change
 * - Light auth guard (doesn't hard-block; just hides content if not resolved)
 */
export default function Settings() {
  const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
  const user = useMemo(() => RESOLVE_LOCAL_USER_STATE(), []);

  const isAuthed = Boolean(auth?.token);

  const navItems = [
    {
      to: "profile-change",
      label: "Profile",
      description: "Update your bio, CV, website, and picture.",
    },
    {
      to: "personal-change",
      label: "Personal information",
      description: "Update your first name, last name, and email address.",
    },
    {
      to: "password-change",
      label: "Password",
      description: "Update your password.",
    },
  ];

  return (
    <div className="settings">
      <header className="settings__header">
        <h1 className="settings__title">Settings</h1>
        <p className="settings__subtitle">
          {user?.username ? `Signed in as ${user.username}` : "—"}
        </p>
      </header>

      <div className="settings__grid">
        {/* Sidebar */}
        <nav className="settings__sidebar" aria-label="Settings sections">
          <ul className="settings__navlist">
            {navItems.map((item) => (
              <li key={item.to} className="settings__navitem">
                <NavLink
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    "settings__link" + (isActive ? " settings__link--active" : "")
                  }
                >
                  <span className="settings__linkLabel">{item.label}</span>
                  <span className="settings__linkDesc">{item.description}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <main className="settings__content" role="main">
          {!isAuthed ? (
            <div className="settings__card settings__card--muted">
              <h3>You’re not signed in.</h3>
              <p>Please sign in to update your settings.</p>
            </div>
          ) : (
            <Routes>
              {/* Default: redirect to a sensible first tab */}
              <Route index element={<Navigate to="profile-change" replace />} />
              <Route path="profile-change" element={<ProfileChange />} />
              <Route path="personal-change" element={<PersonalChange />} />
              <Route path="password-change" element={<PwdChange />} />
              {/* Fallback for unknown subroutes */}
              <Route path="*" element={<Navigate to="profile-change" replace />} />
            </Routes>
          )}
        </main>
      </div>
    </div>
  );
}
